import { Card } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { Button } from '../../../../components/ui/button';
import { Loader, Sparkles } from 'lucide-react';
import { useContext, useState } from 'react';
import { useFormOptions } from 'features/ai-blog-writer/hooks/useFormOptions';
import { BlogContext } from 'features/ai-blog-writer/BlogContext';
import FormSkeleton from '../Skeletons/FormSkeletons';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DEFAULT_BLOG_CONTENT } from 'features/ai-blog-writer';

interface Language {
  name: string;
}
interface Tones {
  name: string;
}
interface Industry {
  name: string;
}

interface FormErrors {
  topic?: string;
  language?: string;
  img_number?: string;
  tone?: string;
  industry?: string;
  min_len?: string;
  max_len?: string;
}

export function BlogGeneratorForm() {
  const { handleGenerateBlog } = useContext(BlogContext);
  const [buttonName, setButtonName] = useState('Generate Blog');
  const { data, isLoading } = useFormOptions();
  const navigate = useNavigate();
  const MAX_IMAGES = 5;
  const [errors, setErrors] = useState<FormErrors>({});
  const { handleUpdateBlogContent } = useContext(BlogContext);
  const [formData, setFormData] = useState({
    topic: '',
    language: '',
    img_number: 1,
    tone: '',
    industry: '',
    min_len: 150,
    max_len: 300,
  });

  const validateForm = () => {
    const newErrors: FormErrors = {};

    // Topic validation
    if (!formData.topic.trim()) {
      newErrors.topic = 'Topic is required';
    } else if (formData.topic.length < 3) {
      newErrors.topic = 'Topic must be at least 3 characters long';
    }

    // Language validation
    if (!formData.language) {
      newErrors.language = 'Language is required';
    }

    // Image number validation
    if (formData.img_number < 0 || formData.img_number > MAX_IMAGES) {
      newErrors.img_number = `Number of images must be between 0 and ${MAX_IMAGES}`;
    }

    // Tone validation
    if (!formData.tone) {
      newErrors.tone = 'Tone is required';
    }

    // Industry validation
    if (!formData.industry) {
      newErrors.industry = 'Industry is required';
    }

    // Length validation
    if (formData.min_len >= formData.max_len) {
      newErrors.min_len = 'Minimum length must be less than maximum length';
      newErrors.max_len = 'Maximum length must be greater than minimum length';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormValidation = () => {
    return (
      formData.topic &&
      formData.min_len &&
      formData.max_len &&
      formData.language &&
      formData.tone &&
      formData.industry &&
      formData.img_number >= 0 &&
      formData.img_number <= MAX_IMAGES
    );
  };

  const handleGenerate = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors before generating');
      return;
    }
    setButtonName('Generating Blog....');
    if (!handleFormValidation()) {
      toast.error(
        'Please fill all details and ensure number of images is between 0 and 5'
      );
      return;
    }

    try {
      handleUpdateBlogContent(DEFAULT_BLOG_CONTENT);
      handleGenerateBlog(formData);
      navigate('/ai-blog-writer/blog');
    } catch (error) {
      console.log(error);
      toast.error('Failed to generate blog. Please try again.');
      setButtonName('Generate Blog');
    }
  };

  const handleImageNumberChange = (value: number) => {
    const newValue = Math.min(Math.max(0, value), MAX_IMAGES);
    setFormData((prevFormData) => ({
      ...prevFormData,
      img_number: newValue,
    }));
  };

  if (isLoading) {
    return <FormSkeleton />;
  }

  return (
    <div className="flex flex-col gap-4 w-full lg:w-[550px] ">
      <div className="flex lg:w-[600px] max-w-[600px] ">
        <div className="text-text-primary mr-[50px] px-6 lg:px-0">
          <div className="text-[15px] "> Start</div>
          <div className="text-[28px] font-bold">Enter blog details</div>
        </div>
      </div>
      <Card className="p-4 md:p-6 bg-background  border border-background-main space-y-4 md:space-y-6 w-full max-w-[90%] mx-auto lg:max-w-[550px] ">
        <div className="space-y-4 text-text-primary">
          {/* Topic Input */}
          <div>
            <label className="text-sm font-medium mb-2 block">Topic</label>
            <Input
              placeholder="Enter topic here"
              onChange={(e) => {
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  topic: e.target.value,
                }));
              }}
              className={`w-full ${errors.topic ? 'border-red-500' : ''}`}
            />
            {errors.topic && (
              <p className="text-red-500 text-xs mt-1">{errors.topic}</p>
            )}
          </div>

          {/* Length Controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">
                Minimum Length
              </label>
              <Select
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    min_len: Number(value),
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full ${errors.min_len ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="300">300 words</SelectItem>
                  <SelectItem value="500">500 words</SelectItem>
                  <SelectItem value="700">700 words</SelectItem>
                </SelectContent>
              </Select>
              {errors.min_len && (
                <p className="text-red-500 text-xs mt-1">{errors.min_len}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Maximum Length
              </label>
              <Select
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    max_len: Number(value),
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full ${errors.max_len ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="500">500 words</SelectItem>
                  <SelectItem value="700">700 words</SelectItem>
                  <SelectItem value="1000">1000 words</SelectItem>
                </SelectContent>
              </Select>
              {errors.max_len && (
                <p className="text-red-500 text-xs mt-1">{errors.max_len}</p>
              )}
            </div>
          </div>

          {/* Language and Tone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Language</label>
              <Select
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    language: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full ${errors.language ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {data?.languages?.map((item: Language, index: number) => (
                    <SelectItem key={index} value={item?.name}>
                      {item?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.language && (
                <p className="text-red-500 text-xs mt-1">{errors.language}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                Tone of Voice
              </label>
              <Select
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    tone: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full ${errors.tone ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {data?.tones?.map((item: Tones, index: number) => (
                    <SelectItem key={index} value={item.name}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tone && (
                <p className="text-red-500 text-xs mt-1">{errors.tone}</p>
              )}
            </div>
          </div>

          {/* Industry and Images */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Industry</label>
              <Select
                onValueChange={(value) => {
                  setFormData((prevFormData) => ({
                    ...prevFormData,
                    industry: value,
                  }));
                }}
              >
                <SelectTrigger
                  className={`w-full ${errors.industry ? 'border-red-500' : ''}`}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {data?.industries?.map((item: Industry, index: number) => (
                    <SelectItem key={index} value={item.name}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.industry && (
                <p className="text-red-500 text-xs mt-1">{errors.industry}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">
                No. of Images (Max {MAX_IMAGES})
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={formData.img_number}
                  onChange={(e) =>
                    handleImageNumberChange(Number(e.target.value))
                  }
                  min={0}
                  max={MAX_IMAGES}
                  className={`flex-1 ${errors.img_number ? 'border-red-500' : ''}`}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handleImageNumberChange(formData.img_number + 1)
                    }
                    disabled={formData.img_number >= MAX_IMAGES}
                    className="bg-secondary text-primary px-3 disabled:opacity-50"
                  >
                    <span className="text-xl">+</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      handleImageNumberChange(formData.img_number - 1)
                    }
                    disabled={formData.img_number <= 0}
                    className="bg-secondary text-primary px-3 disabled:opacity-50"
                  >
                    <span className="text-xl">-</span>
                  </Button>
                </div>
              </div>
              {errors.img_number && (
                <p className="text-red-500 text-xs mt-1">{errors.img_number}</p>
              )}
            </div>
          </div>
        </div>

        <Button
          className="w-full bg-primary text-[14px] lg:text-[16px] hover:bg-primary text-white"
          onClick={handleGenerate}
          disabled={buttonName !== 'Generate Blog'}
        >
          {buttonName === 'Generate Blog' ? (
            <Sparkles className="w-4 h-4 mr-2" />
          ) : (
            <Loader className=" animate-spin w-4 h-4 mr-2" />
          )}
          {buttonName}
        </Button>
      </Card>
    </div>
  );
}
